import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../state/auth-state';
import { getReturnUrl } from '../services/auth-client';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription } from '../components/ui/alert';

/**
 * OAuth2 callback page.
 * Exchanges the authorization code for tokens, then redirects to the saved URL.
 */
export function CallbackPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback, state } = useAuth();
  const processedRef = React.useRef(false);

  React.useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      navigate('/login', { replace: true });
      return;
    }

    if (!code) {
      navigate('/login', { replace: true });
      return;
    }

    handleCallback(code).then(() => {
      const returnUrl = getReturnUrl();
      navigate(returnUrl, { replace: true });
    }).catch(() => {
      navigate('/login', { replace: true });
    });
  }, [searchParams, handleCallback, navigate]);

  if (state.error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center" aria-busy="true">
      <Spinner label="Completing sign in..." />
    </div>
  );
}
